import { isDevEnvironment } from '../../Utils/Window/windowHelpers'
import { logBrazeError } from '../../Utils/analytics/brazeAnalytics'
import { logEvent } from '../../Utils/analytics/firAnalytics'
import { normalizeError } from '../../Utils/errors/errorHelpers'
import { getUniqueInstallId } from '../../Utils/User/userHelpers'
import { ANALYTICS_TAGS } from '../../Constants/analytics'

// Cache for the Braze SDK
let brazeSDKCache: any = null

/**
 * Gets the Braze SDK, caching it after the first import
 * @returns The Braze SDK or null if import fails
 */
export const getBrazeSDK = async () => {
  if (brazeSDKCache) {
    return brazeSDKCache
  }

  try {
    const mod = await import('@braze/web-sdk')
    brazeSDKCache = (mod as any)?.default ?? mod
    return brazeSDKCache
  } catch (error) {
    console.error('Error importing Braze SDK:', error)
    logEvent('brazeError_importSDK_stats', {
      provider: 'braze',
      error,
      product: 'stats',
    })
    return null
  }
}

type BrazeInitConfig = {
  baseUrl: string
  enableLogging: boolean
  minimumIntervalBetweenTriggerActionsInSeconds: number
  allowUserSuppliedJavascript: boolean
}

/**
 * Creates the Braze initialization configuration
 * @returns Braze configuration object
 */
const createBrazeConfig = (): BrazeInitConfig => ({
  baseUrl: process.env.REACT_APP_BRAZE_API_URL || 'sdk.iad-05.braze.com',
  enableLogging: isDevEnvironment(),
  minimumIntervalBetweenTriggerActionsInSeconds: 10,
  allowUserSuppliedJavascript: isDevEnvironment(),
})

/**
 * Initializes the Braze SDK with configuration
 * @param braze Braze SDK instance
 * @returns Whether SDK initialization was successful
 */
const initializeBrazeSDK = (braze: any): boolean => {
  const apiKey = process.env.REACT_APP_BRAZE_API_KEY
  if (!apiKey) {
    throw new Error('Braze API key is required')
  }
  if (isDevEnvironment()) {
    console.log('Braze initialize started')
  }

  const config = createBrazeConfig()
  if (typeof braze.initialize !== 'function') {
    throw new Error('Braze initialize method not found')
  }

  try {
    braze.initialize(apiKey, config)
    return true
  } catch (err) {
    if (isDevEnvironment()) {
      console.error(normalizeError(err)?.message)
    }
    return false
  }
}

/**
 * Sets up Braze user session
 * @param braze Braze SDK instance
 * @param userId Optional user ID for identified users
 * @param preSessionSetup Optional callback to run before opening session (e.g., for subscriptions)
 */
const setupBrazeSession = async (
  braze: any,
  userId?: string,
  preSessionSetup?: (braze: any) => Promise<void> | void
): Promise<void> => {
  try {
    if (userId) {
      if (typeof braze.changeUser !== 'function') {
        throw new Error('Braze changeUser method not found')
      }
      await Promise.resolve(braze.changeUser(userId))
    }

    await new Promise((resolve) => setTimeout(resolve, 100))

    // Ability to run custom pre-session setup
    if (preSessionSetup) {
      await Promise.resolve(preSessionSetup(braze))
    }

    if (typeof braze.openSession !== 'function') {
      throw new Error('Braze openSession method not found')
    }
    braze.openSession()

    // Wait for session before data flush
    await new Promise((resolve) => setTimeout(resolve, 200))

    // After opening the session, request an immediate flush
    try {
      braze?.requestImmediateDataFlush?.()
    } catch (flushError) {
      console.warn('Error requesting Braze data flush after openSession:', flushError)
    }

    // Add Firebase alias after session is established
    void addFirebasePseudoIdAlias(braze)
  } catch (error) {
    console.error('Error setting up Braze session:', error)
    logBrazeError(ANALYTICS_TAGS.braze_setupBrazeSession, {
      error,
    })
  }
}

/**
 * Core function to initialize Braze with common configuration
 * @param userId Optional user ID for identified users
 * @param preSessionSetup Optional callback to run before opening session
 * @returns Whether initialization was successful
 */
export const initBrazeCore = async (
  userId?: string,
  preSessionSetup?: (braze: any) => Promise<void> | void
): Promise<boolean> => {
  if (!process.env.REACT_APP_BRAZE_API_KEY) return false

  try {
    const braze = await getBrazeSDK()
    if (!braze) {
      console.error('Failed to load Braze SDK')
      return false
    }

    const initSuccess = initializeBrazeSDK(braze)
    if (!initSuccess) {
      console.error('Failed to initialize Braze SDK')
      return false
    }

    await setupBrazeSession(braze, userId, preSessionSetup)
    return true
  } catch (error) {
    const context = userId ? 'initBraze' : 'initBrazeAnonymous'
    console.error(`Error initializing Braze in ${context}:`, error)
    logEvent(`brazeError_${context}_stats`, {
      provider: 'braze',
      error,
      product: 'stats',
    })
    return false
  }
}

/**
 * Adds Firebase pseudo ID alias to Braze user
 * @param braze Braze SDK instance
 * @returns Whether alias was added successfully
 */
const addFirebasePseudoIdAlias = async (braze: any): Promise<boolean> => {
  try {
    const installId = await getUniqueInstallId()
    if (!installId) return false

    if (isDevEnvironment()) {
      console.log(
        'Adding firebase_pseudo_id alias to Braze user. Install ID:',
        installId
      )
    }

    const user = braze?.getUser?.()
    if (!user || typeof user.addAlias !== 'function') {
      logBrazeError('brazeError_alias_missing_user_or_method')
      return false
    }

    try {
      const aliasResult = user.addAlias(installId, 'firebase_pseudo_id')

      if (isDevEnvironment()) {
        console.log('Braze addAlias result:', aliasResult)
      }

      return Boolean(aliasResult)
    } catch (aliasError) {
      console.error('Error calling addAlias:', aliasError)
      logBrazeError('brazeError_addAlias_call_failed', {
        error: normalizeError(aliasError)?.message,
        installId,
      })
      return false
    }
  } catch (error) {
    console.error('Error adding alias to Braze user:', error)
    logBrazeError('brazeError_alias_exception', {
      error: normalizeError(error)?.message,
    })
    return false
  }
}

/**
 * Signs out the current user from Braze
 * @returns Whether the sign out was successful
 */
export const signOutBraze = async (): Promise<boolean> => {
  try {
    const braze = await getBrazeSDK()
    if (!braze) return false

    if (typeof braze.wipeData !== 'function') {
      throw new Error('Braze wipeData method not found')
    }

    braze.wipeData()

    return true
  } catch (error) {
    logEvent('brazeError_signOut_stats', {
      provider: 'braze',
      error,
      product: 'stats',
    })
    return false
  }
}
